import { Component } from "@kluntje/core";
import { fetchJSON } from "Helper/apiHelper";
import { render } from "lit-html";
import { shopApiResponse } from "Interfaces/shopApiResponse";
import { I18nService } from "Services/I18nService";

import { shopAccordionTemplate } from "./fra-m-shop__accordion.template"; // eslint-disable-line

/**
 * Custom Element to handle shop form submission
 */
class FRAShop extends Component {
  constructor() {
    super({
      ui: {
        form: ".fra-js-shop__form :-one",
        submitBtn: ".fra-js-shop__submit :-one",
        inputs: ".fra-js-shop__input",
        dropDowns: ".fra-e-dropdown",
        shopAccordion: ".fra-js-shop__accordion .fra-js-shop-accordion :-one",
      },
      initialStates: {
        loaded: false,
        shopDetails: {},
      },
      reactions: {
        shopDetails: ["renderAccordion"],
      },
      events: [
        {
          event: "submit",
          target: "form",
          handler: "handleSubmit",
        },
      ],
    });
  }

  /**
   * Returns provided api-url from custom element
   * @returns {string}
   */
  get initialUrl(): string {
    return this.getAttribute("data-initial-url") || "";
  }

  /**
   * Returns given submit-url
   * @returns {string}
   */
  get submitUrl(): string {
    return this.ui.form.getAttribute("action") || "";
  }

  /**
   * Lifecycle-Hook
   * @override
   */
  afterComponentRender(): void {
    this.fetchInitialData();
  }

  /**
   * Fetch data from API and store response in state
   */
  async fetchInitialData(): Promise<void> {
    const apiResponse = await fetchJSON(this.initialUrl);
    this.setState({ shopDetails: apiResponse });
  }

  /**
   * handles submit event of form
   * @param {Event} _e
   */
  handleSubmit(_e?: Event): void {
    if (_e) {
      _e.preventDefault();
    }

    this.fetchResults();

    // remove focus-pseudoclass
    this.ui.submitBtn.blur();
  }

  /**
   * Fetch data from API and return response
   */
  async fetchResults(): Promise<void> {
    const submitFormData = this.prepareSubmitData();

    if (submitFormData === null || this.submitUrl === "") return;

    await fetchJSON(this.generateRequest(submitFormData))
      .then(response => {
        this.setState({ loaded: true });
        // this.renderResult(response.data);
        this.setState({ shopDetails: response });
      })
      .catch(error => {
        console.warn(`An error occurred while loading the result: ${error}`);
        return "";
      });
  }

  /**
   * Prepares data for submit
   * @returns {object | null}
   */
  prepareSubmitData(): object | null {
    const submitData: any = {};
    let hasValues = false;
    for (const inp of this.ui.inputs) {
      const curSubmitData = inp;
      if (curSubmitData) {
        if (
          (inp.type === "radio" && inp.checked) ||
          (inp.type === "text" && inp.value) ||
          (inp.type === "hidden" && inp.value)
        ) {
          submitData[curSubmitData.name] = curSubmitData.value;
          hasValues = true;
        }
      }
    }
    if (hasValues) return submitData;

    return null;
  }

  /**
   * Generate request to fetch data from
   * @param {object} data
   * @returns {string}
   */
  generateRequest(data: object): string {
    const resultJson = JSON.stringify(data);
    let requestString = "";
    JSON.parse(resultJson, (key, value) => {
      if (value.length > 0) {
        requestString += `${key}=${value}/`; // separator "/" instead of "&" because of backend-suffix-approach
      }
    });
    requestString = requestString.substring(0, requestString.length - 1);
    return `${this.submitUrl}/${requestString}`; // separator "/" instead of "?" because of backend-suffix-approach
  }

  /**
   * Rendering Accordion with lit
   */
  async renderAccordion(): Promise<void> {
    await I18nService.getInstance().waitForJson();
    const input = shopAccordionTemplate(this.state.shopDetails as shopApiResponse);
    render(input, this.ui.shopAccordion);
    this.ui.shopAccordion.update();
  }
}

customElements.define("fra-m-shop", FRAShop);

export default FRAShop;
